import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { IdentityHub } from "@/components/profile/IdentityHub";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { AchievementsCenter } from "@/components/profile/AchievementsCenter";
import { MyOffersWallet } from "@/components/wallet/MyOffersWallet";
import { UserIdentityCard } from "@/components/profile/UserIdentityCard";
import { FanTitleCard } from "@/components/social/FanTitleCard";
import { FanPointsProgressBar } from "@/components/profile/FanPointsProgressBar";
import { markWalletSeen } from "@/lib/walletDiscovery";
import { ChallengeFriendButton } from "@/components/challenges/ChallengeFriendButton";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { notifyNationChanged, useSelectedNation } from "@/hooks/useSelectedNation";

export const Route = createFileRoute("/mi-identidad")({
  head: () => ({
    meta: PREVIEW_MODE
      ? [
          { title: "¿Con quién vivirás esta Final? · VerseSport" },
          {
            name: "description",
            content:
              "Elige tu selección para vivir la Final del Mundial con VerseSport de tu lado.",
          },
          { property: "og:title", content: "¿Con quién vivirás esta Final? · VerseSport" },
          {
            property: "og:description",
            content: "España o Argentina. Un toque. Vive la Final con VerseSport.",
          },
        ]
      : [
          { title: "Mi Identidad · VerseSport" },
          {
            name: "description",
            content:
              "Tu identidad futbolística: equipo, colores en vivo, personalidad y nivel dentro de VerseSport.",
          },
          { property: "og:title", content: "Mi Identidad · VerseSport" },
          {
            property: "og:description",
            content:
              "Vive el partido con los colores de tu equipo. Personaliza tu identidad emocional.",
          },
        ],
  }),
  component: IdentityPage,
});

function IdentityPage() {
  if (PREVIEW_MODE) return <FinalIdentityPage />;
  return <FullIdentityPage />;
}

/* ---------------------------------------------------------------------------
 * PREVIEW MODE — World Cup Final selector (ESP vs ARG)
 * ------------------------------------------------------------------------- */

type Code = "ESP" | "ARG";

const FINALISTS: Array<{
  code: Code;
  name: string;
  flag: string;
  accent: string;
  welcome: string;
}> = [
  {
    code: "ESP",
    name: "España",
    flag: "🇪🇸",
    accent: "#AA151B",
    welcome: "🇪🇸 Hoy jugamos con España.",
  },
  {
    code: "ARG",
    name: "Argentina",
    flag: "🇦🇷",
    accent: "#75AADB",
    welcome: "🇦🇷 Hoy jugamos con Argentina.",
  },
];

function FinalIdentityPage() {
  const navigate = useNavigate();
  const { user, authReady } = useAuth();
  const { nation } = useSelectedNation();
  const [saving, setSaving] = useState<Code | null>(null);
  const [welcome, setWelcome] = useState<{ code: Code; text: string } | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    void trackEvent("identity_experience_entered", {});
  }, []);

  const pick = async (code: Code) => {
    if (saving || leaving) return;
    setSaving(code);
    const meta = FINALISTS.find((f) => f.code === code)!;
    try {
      if (user?.id) {
        await supabase.from("profiles").update({ selection_team: code }).eq("id", user.id);
      }
      try {
        window.localStorage.setItem("vs:final:nation", code);
      } catch { /* noop */ }
      try {
        const { setFanContext } = await import("@/lib/adaptiveFan/adaptiveFanProfile");
        setFanContext({ favoriteTeam: code });
      } catch { /* ignore */ }
      notifyNationChanged();
      void trackEvent("nation_selected", { surface: "mi_identidad_final", nation: code });
    } finally {
      setSaving(null);
    }
    setWelcome({ code, text: meta.welcome });
    setLeaving(true);
    window.setTimeout(() => {
      void navigate({ to: "/" });
    }, 900);
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-background">
      <RouteTopBar fallback="/" />
      <div
        className={`mx-auto flex min-h-[calc(100vh-56px)] max-w-2xl flex-col px-4 py-8 transition-opacity duration-500 sm:px-6 ${
          leaving ? "opacity-0" : "opacity-100"
        }`}
      >
        <header className="mb-8 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-primary">
            Final · Hoy
          </p>
          <h1 className="mt-2 text-3xl font-black leading-tight sm:text-4xl">
            ¿Con quién vivirás esta Final?
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Un toque. VerseSport se pone de tu lado.
          </p>
        </header>

        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
          {FINALISTS.map((f) => {
            const active = nation === f.code;
            const isSaving = saving === f.code;
            return (
              <button
                key={f.code}
                type="button"
                disabled={!authReady || saving !== null}
                onClick={() => pick(f.code)}
                aria-label={`Vivir la Final con ${f.name}`}
                className="group relative flex flex-col items-center justify-center gap-3 overflow-hidden rounded-3xl border-2 p-8 text-center transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                style={{
                  borderColor: active ? f.accent : `${f.accent}55`,
                  background: `radial-gradient(120% 90% at 50% 0%, ${f.accent}22 0%, transparent 70%)`,
                  boxShadow: active ? `0 0 0 2px ${f.accent}, 0 20px 60px -20px ${f.accent}` : undefined,
                }}
              >
                <span className="text-7xl leading-none sm:text-8xl" aria-hidden>
                  {f.flag}
                </span>
                <span className="text-2xl font-black tracking-tight sm:text-3xl">
                  {f.name}
                </span>
                <span
                  className="text-[11px] font-black uppercase tracking-[0.2em]"
                  style={{ color: f.accent }}
                >
                  {isSaving ? "Entrando…" : active ? "Tu selección" : "Vivir la Final"}
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-8 text-center text-[11px] text-muted-foreground">
          Podrás cambiar tu selección en cualquier momento.
        </p>
      </div>

      {welcome && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center px-6"
        >
          <div
            className="animate-in fade-in zoom-in-95 rounded-2xl border-2 bg-background/95 px-8 py-6 text-center shadow-2xl backdrop-blur duration-300"
            style={{
              borderColor: FINALISTS.find((f) => f.code === welcome.code)!.accent,
              boxShadow: `0 0 60px -10px ${FINALISTS.find((f) => f.code === welcome.code)!.accent}`,
            }}
          >
            <p className="text-xl font-black leading-tight sm:text-2xl">
              {welcome.text}
            </p>
          </div>
        </div>
      )}
    </main>
  );
}

/* ---------------------------------------------------------------------------
 * FULL MODE — Original multi-module identity hub (preserved for post-preview)
 * ------------------------------------------------------------------------- */

function FullIdentityPage() {
  const navigate = useNavigate();
  useEffect(() => {
    markWalletSeen();
  }, []);
  const enter = () => {
    void trackEvent("identity_experience_entered", {});
    void navigate({ to: "/" });
  };
  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/" />
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <header className="mb-5">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
            VerseSport
          </p>
          <h1 className="mt-1 text-3xl font-black leading-tight">
            Mi Identidad
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Quién eres dentro del fútbol. Tu equipo, tu energía, tu estilo.
          </p>
        </header>
        <FanPointsProgressBar className="mb-5" />

        <SectionLabel
          eyebrow="01 · Quién eres"
          title="Tu equipo y tu personalidad"
          hint="Elige los colores, narrativa y modo con los que vives el partido."
        />
        <IdentityHub />

        <SectionLabel
          className="mt-7"
          eyebrow="02 · Tu estilo de fan"
          title="Identidad adaptativa"
          hint="Cómo te clasifica VerseSport según tu juego real."
        />
        <div className="space-y-4">
          <UserIdentityCard />
          <FanTitleCard />
        </div>

        <SectionLabel
          className="mt-7"
          eyebrow="03 · Tus logros"
          title="Trofeos, títulos y badges"
          hint="Todo lo que has desbloqueado por jugar y compartir."
        />
        <AchievementsCenter />

        <SectionLabel
          className="mt-7"
          eyebrow="04 · Reta a un amigo"
          title="Friend vs Friend"
          hint="Duelo privado de 24 horas. Quien acierte más predicciones gana +100 XP y el badge FRIEND CHAMPION."
        />
        <div className="rounded-2xl border border-border bg-background/60 p-3">
          <ChallengeFriendButton className="w-full" />
        </div>

        <SectionLabel
          className="mt-7"
          eyebrow="05 · Tus recompensas"
          title="Mi Wallet"
          hint="Cupones y ofertas activas de patrocinadores."
        />
        <MyOffersWallet />

        <div className="mt-6">
          <button
            type="button"
            onClick={enter}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow hover:opacity-95"
          >
            Entrar a la experiencia
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </main>
  );
}

function SectionLabel({
  eyebrow,
  title,
  hint,
  className = "",
}: {
  eyebrow: string;
  title: string;
  hint?: string;
  className?: string;
}) {
  return (
    <div className={`mb-3 ${className}`}>
      <div className="text-[9px] font-black uppercase tracking-[0.2em] text-primary/80">
        {eyebrow}
      </div>
      <div className="mt-0.5 text-base font-black leading-tight">{title}</div>
      {hint && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}
